import { type Action, createAction, createSlice } from '@reduxjs/toolkit'

import {
  checkCustomAudienceCreation,
  createCustomAudience,
  createLookalikeAudience,
  fetchCustomAudiences,
  fetchCustomAudiencesForIg,
  fetchLookalikeSourcePages,
  getCustomAudienceUserActionNames,
  getCustomAudienceUserActionNamesForIg,
  searchCountries,
} from 'apps/ads/actions/customAudienceActionsTyped'
import * as atypes from 'apps/ads/constants/AdsReduxActionTypes'
import { CustomAudienceCreateModalState, CustomAudienceState } from 'apps/ads/models/adsInterfaces'

export const initialState: CustomAudienceState = {
  createModal: {
    type: null,
    name: '',
    filter: null,
    accountId: '',
    locations: [],
    ratio: 1,
    sourceId: null,
    isReady: false,
    isCreating: false,
  },
  byAccount: {},
  countriesSearch: {},
  accountsByUserId: {},
  customAudiencesByUserId: {},
  llaPagesByBusiness: {},
}

const modalReset = createAction(atypes.CUSTOM_AUDIENCE_CREATE_MODAL_RESET)

interface ModalUpdateAction extends Action<string> {
  changes: Partial<CustomAudienceCreateModalState>
}

const isModalUpdate = (action: Action): action is ModalUpdateAction =>
  action.type === atypes.CUSTOM_AUDIENCE_CREATE_MODAL_UPDATE

type AudiencesFetchPending =
  | ReturnType<typeof fetchCustomAudiences.pending>
  | ReturnType<typeof fetchCustomAudiencesForIg.pending>

type AudiencesFetchFulfilled =
  | ReturnType<typeof fetchCustomAudiences.fulfilled>
  | ReturnType<typeof fetchCustomAudiencesForIg.fulfilled>

type AudiencesFetchRejected =
  | ReturnType<typeof fetchCustomAudiences.rejected>
  | ReturnType<typeof fetchCustomAudiencesForIg.rejected>

type UserActionNamesFulfilled =
  | ReturnType<typeof getCustomAudienceUserActionNames.fulfilled>
  | ReturnType<typeof getCustomAudienceUserActionNamesForIg.fulfilled>

type CreateAudiencePending =
  | ReturnType<typeof createCustomAudience.pending>
  | ReturnType<typeof createLookalikeAudience.pending>

type CreateAudienceFulfilled =
  | ReturnType<typeof createCustomAudience.fulfilled>
  | ReturnType<typeof createLookalikeAudience.fulfilled>

type CreateAudienceRejected =
  | ReturnType<typeof createCustomAudience.rejected>
  | ReturnType<typeof createLookalikeAudience.rejected>

const isAudiencesFetchPending = (action: Action): action is AudiencesFetchPending =>
  fetchCustomAudiences.pending.match(action) || fetchCustomAudiencesForIg.pending.match(action)

const isAudiencesFetchFulfilled = (action: Action): action is AudiencesFetchFulfilled =>
  fetchCustomAudiences.fulfilled.match(action) || fetchCustomAudiencesForIg.fulfilled.match(action)

const isAudiencesFetchRejected = (action: Action): action is AudiencesFetchRejected =>
  fetchCustomAudiences.rejected.match(action) || fetchCustomAudiencesForIg.rejected.match(action)

const isUserActionNamesFulfilled = (action: Action): action is UserActionNamesFulfilled =>
  getCustomAudienceUserActionNames.fulfilled.match(action) ||
  getCustomAudienceUserActionNamesForIg.fulfilled.match(action)

const isCreateAudiencePending = (action: Action): action is CreateAudiencePending =>
  createCustomAudience.pending.match(action) || createLookalikeAudience.pending.match(action)

const isCreateAudienceFulfilled = (action: Action): action is CreateAudienceFulfilled =>
  createCustomAudience.fulfilled.match(action) || createLookalikeAudience.fulfilled.match(action)

const isCreateAudienceRejected = (action: Action): action is CreateAudienceRejected =>
  createCustomAudience.rejected.match(action) || createLookalikeAudience.rejected.match(action)

const customAudienceSlice = createSlice({
  name: 'customAudience',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(modalReset, (state) => {
        state.createModal = {
          ...initialState.createModal,
          accountId: state.createModal.accountId,
        }
      })
      .addCase(fetchLookalikeSourcePages.pending, (state, action) => {
        const businessId = action.meta.arg
        state.llaPagesByBusiness[businessId] = {
          ...state.llaPagesByBusiness[businessId],
          loading: true,
        }
      })
      .addCase(fetchLookalikeSourcePages.fulfilled, (state, action) => {
        const businessId = action.meta.arg
        state.llaPagesByBusiness[businessId] = {
          list: action.payload.pages ?? [],
          loading: false,
        }
      })
      .addCase(fetchLookalikeSourcePages.rejected, (state, action) => {
        const businessId = action.meta.arg
        state.llaPagesByBusiness[businessId] = {
          ...state.llaPagesByBusiness[businessId],
          loading: false,
        }
      })
      .addCase(checkCustomAudienceCreation.pending, (state) => {
        state.createModal.isChecking = true
      })
      .addCase(checkCustomAudienceCreation.fulfilled, (state) => {
        state.createModal.isChecking = false
      })
      .addCase(checkCustomAudienceCreation.rejected, (state) => {
        state.createModal.isChecking = false
      })
      .addCase(searchCountries.fulfilled, (state, action) => {
        state.countriesSearch[action.meta.arg ?? ''] = action.payload.data
      })
      .addMatcher(isCreateAudiencePending, (state) => {
        state.createModal.isCreating = true
      })
      .addMatcher(isCreateAudienceFulfilled, (state, action) => {
        state.createModal.isCreating = false
        if (action.payload) {
          state.createModal.isReady = true
        }
      })
      .addMatcher(isCreateAudienceRejected, (state) => {
        state.createModal.isCreating = false
      })
      .addMatcher(isAudiencesFetchPending, (state, action) => {
        const accountId = action.meta.arg
        state.byAccount[accountId] = {
          ...state.byAccount[accountId],
          loading: true,
        }
      })
      .addMatcher(isAudiencesFetchFulfilled, (state, action) => {
        const accountId = action.meta.arg
        state.byAccount[accountId] = {
          list: action.payload.data ?? [],
          loading: false,
        }
      })
      .addMatcher(isAudiencesFetchRejected, (state, action) => {
        const accountId = action.meta.arg
        state.byAccount[accountId] = {
          ...state.byAccount[accountId],
          loading: false,
        }
      })
      .addMatcher(isUserActionNamesFulfilled, (state, action) => {
        const { userId, accountId, customAudienceId } = action.meta.arg
        state.accountsByUserId[userId] = {
          ...state.accountsByUserId[userId],
          [accountId]: action.payload.ad_account_name ?? '',
        }
        state.customAudiencesByUserId[userId] = {
          ...state.customAudiencesByUserId[userId],
          [customAudienceId]: action.payload.custom_audience_name ?? '',
        }
      })
      .addMatcher(isModalUpdate, (state, action) => {
        state.createModal = { ...state.createModal, ...action.changes }
      })
  },
})

export default customAudienceSlice.reducer
